import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import Spinner from "../components/Spinner";

const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch categories
      const categoriesRes = await axios.get(
        PRODUCT_API + "/products/categories/",
      );
      const categoryList = Array.isArray(categoriesRes.data?.content)
        ? categoriesRes.data.content
        : [];
      setCategories(categoryList);

      // Fetch products with filters, sorting, and pagination
      const res = await axios.get(PRODUCT_API + "/products/buyers", {
        params: {
          page,
          size: pageSize,
          sortField: sortField || undefined,
          sortOrder,
          categoryId: selectedCategory || undefined,
        },
      });

      const content = res.data?.content || [];
      setProducts(content);
      setTotalPages(res.data?.totalPages || 0);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setError("Failed to fetch products or categories.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortOrder, selectedCategory, PRODUCT_API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="container-fluid px-0 mt-0 pt-0">
      {/* Hero Banner */}
      <div className="py-5 bg-info text-white text-center shadow-sm">
        <h2 className="fw-bold">Shop the Best Deals Every Day!</h2>
        <p className="mb-0">
          Enjoy discounts, fast delivery, and amazing offers on every product.
        </p>
      </div>

      {/* Main Content */}
      <div className="container-fluid py-5">
        <div className="row">
          {/* Sidebar */}
          <div className="col-12 col-md-3 mb-4">
            {/* Categories */}
            <div className="p-4 border rounded shadow-sm bg-light">
              <h5 className="mb-3 fw-bold">Categories</h5>
              <ul className="list-group list-group-flush">
                <li
                  className={`list-group-item ${
                    selectedCategory === "" ? "active" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedCategory("")}
                >
                  All
                </li>
                {categories.map((cat) => (
                  <li
                    key={cat.id}
                    className={`list-group-item ${
                      selectedCategory === cat.id ? "active" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.name}{" "}
                    <span className="badge bg-secondary">
                      {cat.productCount}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sort */}
            <div className="p-4 border rounded shadow-sm bg-light mt-4">
              <h5 className="mb-3 fw-bold">Sort By</h5>
              <select
                className="form-select"
                value={sortField}
                onChange={(e) => handleSortChange(e.target.value)}
              >
                <option value="">Default</option>
                <option value="price">Price</option>
                <option value="name">Name</option>
                <option value="averageRating">Rating</option>
              </select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="col-12 col-md-9">
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row g-3">
              {loading
                ? Array.from({ length: pageSize }).map((_, idx) => (
                    <div className="col-6 col-sm-4 col-md-3" key={idx}>
                      <div className="card shadow-sm mb-3">
                        <div className="skeleton-image mb-2"></div>
                        <div className="card-body p-2">
                          <div className="skeleton-text mb-1"></div>
                          <div className="skeleton-text short"></div>
                        </div>
                      </div>
                    </div>
                  ))
                : products.map((product) => (
                    <div
                      className="col-6 col-sm-4 col-md-3"
                      key={product.productId}
                    >
                      <ProductCard product={product} />
                    </div>
                  ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="d-flex justify-content-center mt-4">
                <button
                  className="btn btn-outline-primary me-2"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <span className="align-self-center fw-bold">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  className="btn btn-outline-primary ms-2"
                  onClick={() => setPage(page + 1)}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
